import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../lib/api";
import { trackEvent } from "../lib/analytics";
import { useNavigate } from "../lib/router-context";
import { useAppStore } from "../lib/store";
import { getThemeColors } from "../lib/theme";
import {
  getRequestedGameLanguage,
  randomWikipediaArticles,
  searchWikipediaArticles,
  type WikipediaArticle,
} from "../lib/wikipediaApi";
import { ArticleCard } from "../components/ArticleCard";
import { HomeLink } from "../components/HomeLink";
import { SectionHeader } from "../components/SectionHeader";
import { ThemeToggle } from "../components/ThemeToggle";
import { Button } from "../components/ui/Button";
import { SvgIcon } from "../components/ui/SvgIcon";
import boltIcon from "../assets/bolt-icon.svg?raw";
import checkCircleIcon from "../assets/check-circle-icon.svg?raw";

/** Must match `MAX_TARGETS` in the backend's custom_challenges.rs. */
export const MAX_TARGETS = 5;

/** `/create`: build a challenge from any articles and share it as a link. */
export function CreateChallengePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isDark = useAppStore((state) => state.isDark);
  const toggleTheme = useAppStore((state) => state.toggleTheme);
  const lang = useAppStore((state) => state.lang);
  const language = getRequestedGameLanguage(lang);
  const theme = getThemeColors(isDark);

  const [start, setStart] = useState<WikipediaArticle | null>(null);
  const [targets, setTargets] = useState<WikipediaArticle[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdId, setCreatedId] = useState<string | null>(null);

  const usedIds = new Set([start?.id, ...targets.map((target) => target.id)]);
  const canCreate = start !== null && targets.length > 0 && !isCreating;

  const create = async () => {
    if (!start) return;
    setIsCreating(true);
    setError(null);
    try {
      const created = await api.createCustomChallenge(
        language,
        start.id,
        targets.map((target) => target.id),
      );
      trackEvent("custom-challenge-created", {
        targets: String(targets.length),
      });
      setCreatedId(created.public_id);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t("errors.createChallenge"),
      );
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className={`min-h-dvh ${theme.bg} font-sans`}>
      <div className={`sticky top-0 z-50 border-b ${theme.border} ${theme.bg}`}>
        <div className="w-full px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
          <HomeLink />
          <ThemeToggle isDark={isDark} onToggle={toggleTheme} />
        </div>
      </div>

      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <p
          className={`text-xs font-medium uppercase tracking-wider mb-4 ${theme.textMuted}`}
        >
          {t("daily.wikipedia", { language: language.toUpperCase() })}
        </p>
        <h1
          className={`text-5xl sm:text-6xl font-bold tracking-tight mb-4 ${theme.text}`}
        >
          {t("create.title")}
        </h1>
        <p className={`text-lg mb-12 ${theme.textSecondary}`}>
          {t("create.subtitle", { max: MAX_TARGETS })}
        </p>

        {createdId ? (
          <CreatedChallenge
            publicId={createdId}
            onPlay={() => navigate(`/play/c/${createdId}`)}
            onCreateAnother={() => {
              setCreatedId(null);
              setStart(null);
              setTargets([]);
            }}
          />
        ) : (
          <div className="space-y-12">
            <section className="space-y-3">
              <SectionHeader
                icon={<SvgIcon markup={boltIcon} size={16} />}
                title={t("create.start")}
                theme={theme}
                isDark={isDark}
              />
              {start ? (
                <ArticleCard
                  article={start}
                  theme={theme}
                  testId="create-start-article"
                  buttons={
                    <RemoveButton
                      label={t("create.change")}
                      onClick={() => setStart(null)}
                      testId="create-change-start-btn"
                    />
                  }
                />
              ) : (
                <ArticlePicker
                  language={language}
                  excludedIds={usedIds}
                  onPick={setStart}
                  testId="create-start-picker"
                />
              )}
            </section>

            <section className="space-y-3">
              <SectionHeader
                icon={<SvgIcon markup={checkCircleIcon} size={16} />}
                title={t("create.targets", {
                  count: targets.length,
                  max: MAX_TARGETS,
                })}
                theme={theme}
                isDark={isDark}
              />
              {targets.map((target, index) => (
                <ArticleCard
                  key={target.id}
                  article={target}
                  theme={theme}
                  testId={`create-target-${index}`}
                  buttons={
                    <RemoveButton
                      label={t("create.remove")}
                      onClick={() =>
                        setTargets(targets.filter((t) => t.id !== target.id))
                      }
                      testId={`create-remove-target-${index}`}
                    />
                  }
                />
              ))}
              {targets.length < MAX_TARGETS && (
                <ArticlePicker
                  language={language}
                  excludedIds={usedIds}
                  onPick={(article) => setTargets([...targets, article])}
                  testId="create-target-picker"
                />
              )}
            </section>

            {error && (
              <p
                className={`rounded-xl px-4 py-3 text-sm ${theme.errorBg} ${theme.errorText}`}
                role="alert"
              >
                {error}
              </p>
            )}

            <Button
              variant="primary"
              isDark={isDark}
              disabled={!canCreate}
              onClick={create}
              data-testid="create-challenge-btn"
            >
              {isCreating ? t("create.creating") : t("create.submit")}
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}

function RemoveButton({
  label,
  onClick,
  testId,
}: {
  label: string;
  onClick: () => void;
  testId: string;
}) {
  const theme = getThemeColors(useAppStore((state) => state.isDark));
  return (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
      className={`shrink-0 text-xs font-medium cursor-pointer hover:underline underline-offset-4 ${theme.textSecondary}`}
      data-testid={testId}
    >
      {label}
    </button>
  );
}

/** Search box with results, plus a random pick for the undecided. */
function ArticlePicker({
  language,
  excludedIds,
  onPick,
  testId,
}: {
  language: string;
  excludedIds: Set<number | undefined>;
  onPick: (article: WikipediaArticle) => void;
  testId: string;
}) {
  const { t } = useTranslation();
  const theme = getThemeColors(useAppStore((state) => state.isDark));
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<WikipediaArticle[]>([]);

  // Results of an older query stay in state but are hidden once it's cleared.
  const visibleResults = query.trim() ? results : [];

  useEffect(() => {
    const trimmed = query.trim();
    if (!trimmed) return;
    let isCancelled = false;
    const timer = setTimeout(() => {
      searchWikipediaArticles(language, trimmed, 1, 6)
        .then((page) => {
          if (!isCancelled) setResults(page.articles);
        })
        .catch(() => {
          if (!isCancelled) setResults([]);
        });
    }, 250);
    return () => {
      isCancelled = true;
      clearTimeout(timer);
    };
  }, [language, query]);

  const pick = (article: WikipediaArticle) => {
    setQuery("");
    setResults([]);
    onPick(article);
  };

  const pickRandom = async () => {
    const articles = await randomWikipediaArticles(language, 5).catch(() => []);
    const article = articles.find((a) => !excludedIds.has(a.id));
    if (article) pick(article);
  };

  return (
    <div className="space-y-2" data-testid={testId}>
      <div
        className={`flex items-center gap-2 rounded-xl border px-4 ${theme.border} ${theme.bgSecondary}`}
      >
        <input
          type="text"
          inputMode="search"
          enterKeyHint="search"
          autoComplete="off"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={t("create.searchPlaceholder")}
          className={`flex-1 py-3 outline-none border-0 focus:ring-0 ${theme.inputBg} ${theme.inputText} ${theme.inputPlaceholder}`}
          data-testid={`${testId}-input`}
        />
        <button
          type="button"
          onClick={pickRandom}
          className={`shrink-0 text-xs font-medium cursor-pointer hover:underline underline-offset-4 ${theme.textSecondary}`}
          data-testid={`${testId}-random`}
        >
          {t("create.random")}
        </button>
      </div>
      {visibleResults.length > 0 && (
        <ul
          className={`rounded-xl border overflow-hidden ${theme.border} ${theme.bgSecondary}`}
        >
          {visibleResults.map((article) => {
            const isUsed = excludedIds.has(article.id);
            return (
              <li key={article.id}>
                <button
                  type="button"
                  disabled={isUsed}
                  onClick={() => pick(article)}
                  className={`w-full text-left px-4 py-3 cursor-pointer disabled:cursor-not-allowed disabled:opacity-40 ${theme.cardHover}`}
                  data-testid={`${testId}-result`}
                >
                  <span className={`block font-medium ${theme.text}`}>
                    {article.title}
                  </span>
                  {article.description && (
                    <span
                      className={`block text-sm truncate ${theme.textMuted}`}
                    >
                      {article.description}
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function CreatedChallenge({
  publicId,
  onPlay,
  onCreateAnother,
}: {
  publicId: string;
  onPlay: () => void;
  onCreateAnother: () => void;
}) {
  const { t } = useTranslation();
  const isDark = useAppStore((state) => state.isDark);
  const theme = getThemeColors(isDark);
  const [copied, setCopied] = useState(false);
  const link = `${window.location.origin}/c/${publicId}`;

  const copy = async () => {
    trackEvent("custom-challenge-copy-link");
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="created-challenge">
      <p className={`text-lg ${theme.text}`}>{t("create.created")}</p>
      <div
        className={`flex items-center gap-3 rounded-xl border px-4 py-3 ${theme.border} ${theme.bgSecondary}`}
      >
        <input
          readOnly
          value={link}
          onFocus={(event) => event.target.select()}
          className={`flex-1 min-w-0 font-mono text-sm outline-none border-0 focus:ring-0 ${theme.inputBg} ${theme.inputText}`}
          data-testid="created-challenge-link"
        />
        <button
          type="button"
          onClick={copy}
          className={`shrink-0 text-sm font-medium cursor-pointer hover:underline underline-offset-4 ${theme.text}`}
          data-testid="copy-challenge-link-btn"
        >
          {copied ? t("daily.copied") : t("create.copyLink")}
        </button>
      </div>
      <div className="flex flex-col sm:flex-row gap-4">
        <Button
          variant="primary"
          isDark={isDark}
          onClick={onPlay}
          data-testid="play-created-challenge-btn"
        >
          {t("create.playNow")}
        </Button>
        <Button
          variant="secondary"
          isDark={isDark}
          onClick={onCreateAnother}
          data-testid="create-another-btn"
        >
          {t("create.another")}
        </Button>
      </div>
      <a
        href={`/c/${publicId}`}
        className={`inline-block text-sm hover:underline underline-offset-4 ${theme.textSecondary}`}
        data-testid="created-challenge-page-link"
      >
        {t("daily.links.challengePage")}
      </a>
    </div>
  );
}
